import { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload as UploadIcon, CheckCircle2, Loader2, AlertTriangle, Zap, ShieldCheck, Lock } from 'lucide-react';
import confetti from 'canvas-confetti';
import { storage, db, auth } from '../lib/firebase';
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { collection, addDoc, serverTimestamp, query, where, getDocs } from "firebase/firestore";
// @ts-ignore
import { pdfjs } from 'react-pdf';

// Fix for pdfjs worker — use static public path, same as Flipbook.tsx
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

interface UploadProps {
    onUploadComplete?: (bookId: string, fileUrl?: string) => void;
}

const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB

// Helper: Generate Thumbnail
const generateThumbnail = async (file: File): Promise<Blob | null> => {
    try {
        const arrayBuffer = await file.arrayBuffer();
        const loadingTask = pdfjs.getDocument(arrayBuffer);
        const pdf = await loadingTask.promise;
        const page = await pdf.getPage(1);

        const viewport = page.getViewport({ scale: 1.0 });
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');

        if (!context) return null;

        canvas.height = viewport.height;
        canvas.width = viewport.width;

        await page.render({
            canvasContext: context,
            viewport: viewport
        }).promise;

        return new Promise((resolve) => {
            canvas.toBlob((blob) => {
                resolve(blob);
            }, 'image/webp', 0.8);
        });
    } catch (error) {
        console.error("Thumbnail generation failed:", error);
        return null;
    }
};

export function Upload({ onUploadComplete }: UploadProps) {
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [completed, setCompleted] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [fileName, setFileName] = useState<string>('');

    const handleUpload = async (file: File) => {
        setUploading(true);
        setProgress(1); // Immediate feedback
        setError(null);
        setFileName(file.name);

        try {
            let user = auth.currentUser;

            // Auth guard: user must be authenticated (handled by App.tsx routing)
            if (!user) {
                throw new Error("Authentication required. Please sign in to upload.");
            }

            const uid = user.uid;
            console.log(`Starting Encrypted Upload for UID: ${uid}`);

            // 0. Duplicate Check
            const booksRef = collection(db, "users", uid, "books");
            const q = query(booksRef,
                where("title", "==", file.name),
                where("size", "==", file.size)
            );

            const existingDocs = await getDocs(q);

            if (!existingDocs.empty) {
                setUploading(false);
                setError("This masterpiece is already in your library! ✨");
                return;
            }

            // 1. Setup Uploads (Parallel Execution)
            const timestamp = Date.now();
            const sanitizedName = file.name.replace(/[^a-zA-Z0-9.]/g, '_');
            const storagePath = `users/${uid}/books/${timestamp}_${sanitizedName}`;

            // A. Start Book Upload Immediatey
            const storageRef = ref(storage, storagePath);
            const uploadTask = uploadBytesResumable(storageRef, file);

            // B. Start Thumbnail Gen & Upload Immediately (Non-blocking for progress)
            const thumbnailPromise = (async () => {
                try {
                    const coverBlob = await generateThumbnail(file);
                    if (coverBlob) {
                        const coverPath = `users/${uid}/covers/${timestamp}_${sanitizedName}.webp`;
                        const coverRef = ref(storage, coverPath);
                        await uploadBytesResumable(coverRef, coverBlob);
                        return await getDownloadURL(coverRef);
                    }
                } catch (thumbError) {
                    console.warn("Thumbnail generation skipped:", thumbError);
                }
                return null;
            })();

            // Hook up progress listener
            uploadTask.on('state_changed',
                (snapshot) => {
                    const p = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                    setProgress(p);
                },
                (error) => {
                    console.error("Storage Error:", error);
                    setError(`Secure Storage Failed: ${error.message}`);
                    setUploading(false);
                },
                async () => {
                    // Upload completed
                    try {
                        const [downloadURL, coverUrl] = await Promise.all([
                            getDownloadURL(uploadTask.snapshot.ref),
                            thumbnailPromise
                        ]);

                        // 2. Save Metadata to Firestore
                        const docRef = await addDoc(collection(db, "users", uid, "books"), {
                            title: file.name,
                            size: file.size,
                            file_url: downloadURL,
                            cover_url: coverUrl,
                            created_at: new Date().toISOString(),
                            server_timestamp: serverTimestamp(),
                            is_active: true
                        });

                        finishUpload(docRef.id, downloadURL);

                    } catch (dbError: any) {
                        console.error("Database Error:", dbError);
                        setError(`Metadata Save Failed: ${dbError.message}`);
                        setUploading(false);
                    }
                }
            );

        } catch (error: any) {
            console.error("CRITICAL UPLOAD FAILURE:", error);
            setError(`Unexpected Error: ${error.message}`);
            setUploading(false);
        }
    };

    const onDrop = useCallback((acceptedFiles: File[], fileRejections: any[]) => {
        setError(null);

        if (fileRejections.length > 0) {
            const rejections = fileRejections[0];
            setError(rejections.errors[0].message);
            return;
        }

        if (acceptedFiles.length > 0) {
            const selectedFile = acceptedFiles[0];
            if (selectedFile.type !== 'application/pdf') {
                setError("Only PDF files are supported.");
                return;
            }
            // Auto-start upload
            handleUpload(selectedFile);
        }
    }, []);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: { 'application/pdf': ['.pdf'] },
        maxFiles: 1,
        maxSize: MAX_FILE_SIZE,
        disabled: uploading || completed
    });

    const triggerConfetti = () => {
        confetti({
            particleCount: 150,
            spread: 90,
            origin: { y: 0.6 },
            colors: ['#3b82f6', '#8b5cf6', '#f97316'] // Brand colors
        });
    };

    const finishUpload = (id: string, url?: string) => {
        setUploading(false);
        setCompleted(true);
        triggerConfetti();

        if (onUploadComplete) {
            setTimeout(() => {
                onUploadComplete(id, url);
            }, 1200);
        } else {
            window.location.reload();
        }
    };

    return (
        <div className="w-full max-w-2xl mx-auto px-4 mb-10">
            <div className={`relative overflow-hidden rounded-3xl border transition-all duration-300 ${isDragActive ? 'border-brand-primary bg-slate-800/90 scale-[1.02]' : 'border-slate-800 bg-slate-900/60'
                } backdrop-blur-xl shadow-2xl p-8 md:p-12 text-center group`}>

                {!completed ? (
                    <>
                        {/* Universal / Tech Header */}
                        <div className="mb-6">
                            <div className="flex justify-center gap-3 mb-4">
                                <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-800 text-xs font-medium text-slate-400">
                                    <Zap className="w-3 h-3 text-yellow-500" />
                                    Encrypted Processing
                                </span>
                                <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-green-500/10 border border-green-500/20 text-xs font-medium text-green-400 whitespace-nowrap">
                                    <ShieldCheck className="w-3 h-3" />
                                    Secure & Private Access
                                </span>
                            </div>
                            <h2 className="text-3xl font-bold text-white mb-2">
                                Drop to Read
                            </h2>
                            <p className="text-slate-400 text-sm">
                                Standard Account • Max 50MB
                            </p>
                        </div>

                        {!uploading ? (
                            <div
                                {...getRootProps()}
                                className={`border-2 border-dashed rounded-2xl h-48 flex flex-col items-center justify-center cursor-pointer transition-all duration-300 ${isDragActive
                                    ? 'border-brand-primary bg-brand-primary/5 shadow-[0_0_30px_rgba(59,130,246,0.1)]'
                                    : 'border-slate-700 hover:border-brand-primary/50 hover:bg-slate-800/50'
                                    }`}
                            >
                                <input {...getInputProps()} />
                                <div className={`p-4 rounded-full bg-slate-800 mb-4 transition-transform duration-300 group-hover:scale-110 ${isDragActive ? 'bg-brand-primary/20 text-brand-primary' : 'text-slate-400 group-hover:text-brand-primary'}`}>
                                    <UploadIcon className="w-8 h-8" />
                                </div>
                                <p className="text-slate-300 font-medium group-hover:text-white transition-colors">
                                    {isDragActive ? "Release to Upload" : "Drag & Drop PDF here"}
                                </p>
                            </div>
                        ) : (
                            <div className="bg-slate-800/50 rounded-2xl p-8 border border-slate-700/50 animate-in fade-in zoom-in-95 duration-300">
                                <div className="flex flex-col items-center mb-6">
                                    <div className="relative">
                                        <Loader2 className="w-10 h-10 text-brand-primary animate-spin mb-4" />
                                        <div className="absolute inset-0 flex items-center justify-center -mt-4">
                                            <Lock className="w-4 h-4 text-brand-primary/50 animate-pulse" />
                                        </div>
                                    </div>
                                    <h3 className="text-white font-medium text-lg">{fileName}</h3>
                                    <p className="text-sm text-slate-400">Encrypting & Storing...</p>
                                </div>

                                <div className="w-full max-w-sm mx-auto">
                                    <div className="flex justify-between text-xs text-slate-400 mb-2">
                                        <span className="flex items-center gap-1.5 text-brand-primary/80">
                                            <Lock className="w-3 h-3" />
                                            End-to-End Encrypted
                                        </span>
                                        <span>{Math.round(progress)}%</span>
                                    </div>
                                    <div className="h-2 bg-slate-700/50 rounded-full overflow-hidden">
                                        <div
                                            className="h-full bg-gradient-to-r from-brand-primary to-purple-500 transition-all duration-300 ease-out"
                                            style={{ width: `${progress}%` }}
                                        />
                                    </div>
                                </div>
                            </div>
                        )}

                        {error && (
                            <div className="mt-6 p-4 bg-red-500/10 border border-red-500/20 rounded-xl flex items-center gap-3 text-red-400 text-sm text-left animate-in slide-in-from-top-2">
                                <AlertTriangle className="w-5 h-5 flex-shrink-0" />
                                <span>{error}</span>
                            </div>
                        )}
                    </>
                ) : (
                    <div className="py-8 flex flex-col items-center">
                        <div className="w-24 h-24 bg-green-500/10 rounded-full flex items-center justify-center mb-6 animate-in zoom-in duration-500">
                            <CheckCircle2 className="w-12 h-12 text-green-500" />
                        </div>
                        <h3 className="text-2xl font-bold text-white mb-2">Processing Complete</h3>
                        <p className="text-slate-400 mb-6">Your interactive book is ready.</p>
                        <div className="h-1 w-32 bg-slate-800 rounded-full overflow-hidden">
                            <div className="h-full bg-green-500 animate-pulse w-full"></div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
